import React from 'react';
import { useTranslation } from 'react-i18next';
import { AppstoreOutlined, RobotOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ProjectConfig, DeploymentType } from '../../../types/deployApp.type';

export type ProjectTypeItem = {
  id: string;
  name: string;
  icon: React.ReactNode;
  badge?: string;
  description?: string;
};

export type AvailablePlansData = {
  type: string;
  deploymentType: DeploymentType;
  plans: Array<{ id: string; name: string; price: number | null; features: string[] }>;
} | null;

export interface Step3DeployAppProps {
  config: ProjectConfig;
  projectTypes: ProjectTypeItem[];
  getDeploymentBadge: (type: DeploymentType) => { emoji: string; text: string; color: string } | null;
  selectedPlan: string | null;
  availablePlans: AvailablePlansData;
  getPlanType: (planId: string) => 'shared' | 'dedicated' | null;
  selectedLogsPlan: string | null;
  isSSRTechnology: (type: string, framework: string) => boolean;
  isSSGTechnology: (type: string, framework: string) => boolean;
  backendProjectTypes: ProjectTypeItem[];
  getEstimatedCost: () => string;
}

const Step3DeployApp: React.FC<Step3DeployAppProps> = ({
  config,
  projectTypes,
  getDeploymentBadge,
  selectedPlan,
  availablePlans,
  getPlanType,
  selectedLogsPlan,
  isSSRTechnology,
  isSSGTechnology,
  backendProjectTypes,
  getEstimatedCost,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 dark:bg-dark-bg rounded-lg p-6">
        <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <AppstoreOutlined className="text-xl" />
          {t('deployApp.review.title') || 'Review your configuration'}
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('deployApp.review.appName') || 'App Name'}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('deployApp.review.description') || 'Description'}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.description || t('deployApp.review.noDescription') || 'No description'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('deployApp.review.type') || 'Type'}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {projectTypes.find(p => p.id === config.type)?.name || config.type}
            </span>
          </div>
          {config.runtime && (
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Runtime</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {config.runtime.technology === 'nodejs' ? 'Node.js' :
                 config.runtime.technology === 'netcore' ? '.NET' :
                 config.runtime.technology === 'python' ? 'Python' :
                 config.runtime.technology} {config.runtime.version}
              </span>
            </div>
          )}
          {((isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(bt => bt.id === config.type)) && !isSSGTechnology(config.type, config.framework)) && config.startMode && (
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Modo de Inicio</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.startMode}</span>
            </div>
          )}
          {((isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(bt => bt.id === config.type)) && !isSSGTechnology(config.type, config.framework)) && config.targetMode && (
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">Archivo Objetivo</span>
              <span className="font-medium text-gray-900 dark:text-white">{config.targetMode}</span>
            </div>
          )}
          {config.deploymentType && getDeploymentBadge(config.deploymentType as DeploymentType) && (
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('deployApp.review.deploymentType') || 'Deployment Type'}</span>
              <div className="flex items-center gap-2">
                <span className="text-lg">{getDeploymentBadge(config.deploymentType as DeploymentType)?.emoji}</span>
                <span className={`text-xs px-2 py-0.5 rounded border font-medium ${getDeploymentBadge(config.deploymentType as DeploymentType)?.color}`}>
                  {getDeploymentBadge(config.deploymentType as DeploymentType)?.text}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {config.deploymentType === 'dev' ? t('deployApp.deploymentType.dev.name') :
                    config.deploymentType === 'preview' ? t('deployApp.deploymentType.preview.name') :
                      config.deploymentType === 'prod_lite' ? t('deployApp.deploymentType.prodLite.name') :
                        t('deployApp.deploymentType.prod.name')}
                </span>
              </div>
            </div>
          )}
          {selectedPlan && (
            <>
              <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">{t('deployApp.review.plan') || 'Plan'}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {availablePlans?.plans.find(p => p.id === selectedPlan)?.name || selectedPlan}
                  </span>
                  {getPlanType(selectedPlan) && (
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                      getPlanType(selectedPlan) === 'shared'
                        ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                        : 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300'
                    }`}>
                      {getPlanType(selectedPlan) === 'shared' ? 'Shared' : 'Dedicated'}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
          {config.type === 'html-javascript-ai' && (config as any).aiDescription && (
            <>
              <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <RobotOutlined className="text-purple-600 dark:text-purple-400" />
                  AI Configuration
                </h4>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">AI Description</span>
                <span className="font-medium text-gray-900 dark:text-white max-w-md text-right text-sm">
                  {(config as any).aiDescription?.substring(0, 150)}{(config as any).aiDescription?.length > 150 ? '...' : ''}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">Design Style</span>
                <span className="font-medium text-gray-900 dark:text-white capitalize">
                  {(config as any).aiStyle || 'Modern'}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                <span className="text-gray-600 dark:text-gray-400">Color Palette</span>
                <span className="font-medium text-gray-900 dark:text-white capitalize">
                  {(config as any).aiColors || 'Blue'}
                </span>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-2">
                  <InfoCircleOutlined className="text-purple-600 dark:text-purple-400 text-lg mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-purple-900 dark:text-purple-300 mb-1">
                      Generation Process
                    </p>
                    <p className="text-xs text-purple-800 dark:text-purple-200">
                      Your website will be generated automatically after payment confirmation.
                      This process takes 2-3 minutes. You'll be able to edit it with our visual editor once it's ready.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('deployApp.review.environment') || 'Environment'}</span>
            <span className="font-medium text-gray-900 dark:text-white">{config.environment || t('deployApp.review.notSpecified') || '(no especificado)'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
            <span className="text-gray-600 dark:text-gray-400">{t('deployApp.review.environmentVariables') || 'Environment Variables'}</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {config.environmentVariables.length === 0
                ? t('deployApp.review.noEnvVars') || 'None'
                : `${config.environmentVariables.length} ${config.environmentVariables.length === 1 ? t('deployApp.review.variable') || 'variable' : t('deployApp.review.variables') || 'variables'}`
              }
            </span>
          </div>
          {config.environmentVariables.length > 0 && (
            <div className="pl-4 space-y-2 pt-2">
              {config.environmentVariables.map((envVar, index) => (
                <div key={index} className="flex justify-between py-1 text-sm border-b border-gray-100 dark:border-gray-700">
                  <span className="font-mono text-gray-600 dark:text-gray-400">{envVar.name}</span>
                  <span className="font-mono text-gray-700 dark:text-gray-300">
                    {envVar.hidden ? '••••••••' : envVar.value || '(empty)'}
                  </span>
                </div>
              ))}
            </div>
          )}
          {((isSSRTechnology(config.type, config.framework) || backendProjectTypes.some(bt => bt.id === config.type)) && !isSSGTechnology(config.type, config.framework)) && (
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
              <span className="text-gray-600 dark:text-gray-400">{t('deployApp.review.premiumLogs') || 'Premium Logs'}</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {config.premiumLogs
                  ? selectedLogsPlan
                    ? `${t('deployApp.logs.premium.title')} - ${t(`deployApp.logs.premium.plans.${selectedLogsPlan === 'premium-7days' ? 'plan1' : selectedLogsPlan === 'premium-30days' ? 'plan2' : 'plan3'}.name`)}`
                    : t('deployApp.logs.premium.title') + ' (Plan no seleccionado)'
                  : t('deployApp.logs.standard.title') + ' (Included)'
                }
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-gray-900 dark:text-white">
            {t('deployApp.review.estimatedCost') || 'Estimated Monthly Cost'}
          </h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{getEstimatedCost()}</p>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('deployApp.review.costNote') || 'Based on selected plan. Actual costs may vary based on usage and add-ons.'}
        </p>
      </div>
    </div>
  );
};

export default Step3DeployApp;
